import React, {useState, useRef, useEffect} from 'react';
import style from '../CreateInvoice/CreateInvoice.module.css';
import s from './CreationOfDesign.module.css';
import ButtonPlusElement from '../../Components/Buttons/ButtonPlusElement';
import FunctionalButtonPanel from '../../Components/FunctionalButtonPanel/FunctionalButtonPanel';
import CategorySelectorsList from '../CreateInvoice/components/CategorySelectorsList';
import {Form, Formik} from 'formik';
import BackToOffice from '../../Components/Buttons/BackToOffice';
import {useTranslation} from 'react-i18next';
import SearchByNameInput from '../CreateInvoice/components/SearchByNameInput';
import FilterDay from '../../Components/Filter/Components/FilterDay';
import DepotProductsList from '../../Components/DepotComponents/DepotProductsList';
import {useNavigate, useParams} from 'react-router-dom';
import {useCreateInvoiceDesignForm} from '../../hooks/useCreateInvoiceDesignForm';
import {
	CreateDesignType,
	ProductInOrderRequestResponseType,
	RequestCreateDesignType,
} from '../../types/TypesStore';
import LightGreenButton from '../../Components/Buttons/LightGreenButton';
import VeryLightGreenButton from '../../Components/Buttons/VeryLightGreenButton';
import CreateDesignPopup from './CreateDesignPopup/CreateDesignPopup';
import TopPageInfoBlock from '../../Components/TopPageInfoBlock/TopPageInfoBlock';
import {useCreateActivitiesMutation} from '../../redux/activitiesApi';
import {handleApiCall} from '../../assets/utils/utils';
import {toast} from 'react-toastify';
import infoButton from '../../assets/img/icons/actions/info.svg';
import ClientSelector from './components/ClientSelector/ClientSelector';
import SupplierSelector from './components/SupplierSelector/SupplierSelector';
import ClientTypeCheckButtons from './components/ClientTypeCheckButtons';
import {UserClient} from '../../types/TypesUser';
import {useGetLogisticListQuery} from '../../redux/DepotSlice/depotCashierApi';

const CreationOfDesign = () => {
	const {id} = useParams();
	const {t} = useTranslation();
	const navigate = useNavigate();

	const [create] = useCreateActivitiesMutation();
	const [resetCategoriesKey, setResetCategoriesKey] = useState<number>(0);
	const [selectedClient, setSelectedClient] = useState<UserClient | null>(null);
	const [highlightClientSelector, setHighlightClientSelector] = useState(false);
	const [hasShownClientWarning, setHasShownClientWarning] = useState(false);
	const clientSelectorRef = useRef<HTMLDivElement>(null);
	const [selectedSupplierId, setSelectedSupplierId] = useState<number | null>(
		null,
	);
	const [orderType, setOrderType] = useState<'wholesale' | 'retail'>('wholesale');
	
	// Проверяем наличие фискализации
	const {data: suppliersList} = useGetLogisticListQuery();
	const hasFiscalization = suppliersList?.suppliers && suppliersList.suppliers.length > 0;
	const {
		valueDate,
		setValueDate,
		setCategoryId,
		searchByName,
		selectedProducts,
		createPopup,
		isLoadingProductList,
		productsForRender,
		functionalityButtons,
		data,
		isActivitiesData,
		handleClickButtonInPanel,
		handleChangeProduct,
		handleChangeSearch,
		handleClickReadyButton,
		closePopup,
		setSearchByName,
		// Функциональность сканирования баркодов
		//isScanning,
		isCameraActive,
		cameraPermission,
		showManualInput,
		barcodeInputRef,
		barcodeVideoRef,
		handleBarcodeInputChange,
		startCameraScanning,
		stopCameraScanning,
		addProductByBarcode,
		lastScannedBarcode,
		setShowManualInput,
	} = useCreateInvoiceDesignForm(
		'order',
		id,
		'activities',
		selectedClient?.client_id,
		// Если есть фискализация, используем orderType, иначе тип клиента
		hasFiscalization 
			? (orderType === 'retail' ? 'ordinary' : 'loyal')
			: selectedClient?.type,
	);

	const initialValues: CreateDesignType = {
		order_date:
			data && id && isActivitiesData(data) ? data.order_date : valueDate,
		points: '', // Не сохраняется в activities API
		bonus: '', // Бонусы не сохраняются в activities, всегда пустое
		money: '', // Не сохраняется в activities API
		cashback: '', // Кешбэк не сохраняется в activities, всегда пустое
		client_id:
			data && id && isActivitiesData(data)
				? (data.user_id?.toString() ?? '')
				: '',
		products: data && id ? data.products : [],
		discount: '', // Скидка не сохраняется в activities, всегда пустое
		payment_method:
			data && id && isActivitiesData(data) ? (data.payment_method ?? '') : '',
		status: '', // Не сохраняется в activities API
		typeOrder: 'wholesale', // По умолчанию опт
	};

	const handleSubmit = async (values: CreateDesignType) => {
		const preparedProducts: ProductInOrderRequestResponseType[] =
			selectedProducts.map((p) => ({
				product_id: +p.id,
				count: p.buyQuantity || 0,
			}));

		// Подготавливаем данные клиента из формы или из загруженных данных
		const clientId =
			values.client_id ||
			(data && id && isActivitiesData(data) ? data.user_id?.toString() : '');

		// Определяем тип заказа (retail) на основе выбранного типа или типа клиента
		const isRetail = hasFiscalization 
			? orderType === 'retail' 
			: selectedClient?.type === 'ordinary'; // true для роздріб, false для опт

		const requestData: RequestCreateDesignType = {
			order_date: values.order_date,
			points: +values.points || 0,
			money: +values.money || 0,
			client_id: +clientId,
			payment_method: values.payment_method || '',
			discount: +values.discount || 0,
			cashback: +values.cashback || 0,
			bonus: +values.bonus || 0,
			status: values.status || 'created',
			retail: isRetail, // true якщо роздріб, false якщо опт
			products: preparedProducts,
			supplier_id: selectedSupplierId?.toString() || '', // Используем выбранный ФОП
		};

		const successMessage = id
			? 'Оформлення оновлено успішно'
			: 'Оформлення створено успішно';

		await handleApiCall({
			apiCall: () => create(requestData),
			onSuccess: () => {
				toast.success(successMessage);
				navigate('/paperwork_list');
			},
		});
	};

	// Отслеживаем выбор товаров без клиента
	useEffect(() => {
		if (
			selectedProducts.length > 0 &&
			!selectedClient &&
			!id &&
			!hasShownClientWarning
		) {
			// Показываем подсветку
			setHighlightClientSelector(true);

			// Скроллим к полю выбора клиента
			if (clientSelectorRef.current) {
				clientSelectorRef.current.scrollIntoView({
					behavior: 'smooth',
					block: 'center',
				});
			}

			// Показываем уведомление только один раз
			toast.info(t('Будь ласка, оберіть клієнта для оформлення'));
			setHasShownClientWarning(true);

			// Убираем подсветку через 3 секунды
			const timer = setTimeout(() => {
				setHighlightClientSelector(false);
			}, 3000);

			return () => clearTimeout(timer);
		} else if (selectedClient && highlightClientSelector) {
			// Убираем подсветку, когда клиент выбран
			setHighlightClientSelector(false);
		}
	}, [
		selectedProducts.length,
		selectedClient,
		id,
		highlightClientSelector,
		hasShownClientWarning,
		t,
	]);

	// Обработчик изменения типа заказа (Опт/Роздріб)
	const handleOrderTypeChange = (type: 'wholesale' | 'retail') => {
		setOrderType(type);
		// Товары перезагрузятся автоматически через хук useCreateInvoiceDesignForm
	};

	const resetAllFilters = () => {
		setCategoryId(undefined);
		setResetCategoriesKey((k) => k + 1);
		setSearchByName('');
	};

	const handleToggleCamera = async () => {
		if (isCameraActive) {
			stopCameraScanning();
			toast.info(t('Сканирование з камери зупинено'));
		} else {
			const success = await startCameraScanning();
			if (success) {
				toast.success(t('Камера запущена для сканирования баркодов'));
			} else {
				toast.error(t('Не вдалося запустити камеру'));
			}
		}
	};

	return (
		<main className="page">
			<div className="page__content container">
				<BackToOffice />
				<div
					className={`${style.mainImage} ${style.createDesign} ${s.hideBannerOnMobile}`}
				/>
				<div className={style.title}>
					{id ? t('Редагування оформлення') : t('Створення оформлення')}
				</div>
				<TopPageInfoBlock
					text={
						id
							? t(
									'Редагування існуючого оформлення. Ви можете змінити дату, клієнта, товари та інші параметри. Після збереження зміни будуть застосовані до оформлення.',
								)
							: t(
									'Спочатку оберіть дату проведення. Далі внизу сторінки буде таблиця зі всіма товарами. Ви можете знайти потрібний по пошуку по назві. Також спочатку обрати категорію та підкатегорію. Щоб знов побачити всі треба натиснути "Показати всі". Якщо ви хочете подивитися тільки ті товари які у вас на складі, то кнопка "В наявності". Якщо хочете подивитися які товари ви вже обрали для цього оформлення, то  "Обрані".',
								)
					}
					subText={''}
				/>
				<Formik
					initialValues={initialValues}
					onSubmit={handleSubmit}
					enableReinitialize
				>
					{({handleSubmit, setFieldValue}) => (
						<Form onSubmit={handleSubmit}>
							<div className={style.typeInvoiceSelect}>
								<div className={style.typeInvoiceSelectLeft}>
									<FilterDay
										setFieldValue={setFieldValue}
										setValueDate={setValueDate}
										label={t('Дата проведення оформлення')}
										inputName={'order_date'}
										defaultDate={valueDate}
									/>
								</div>
								{/* Показываем переключатель типа только если есть фискализация */}
								{hasFiscalization && (
									<ClientTypeCheckButtons onTypeChange={handleOrderTypeChange} />
								)}
								<div className={s.typeInvoiceSelectRight}>
									<LightGreenButton
										text={t('Зареєструвати нового клієнта')}
										type={'button'}
										onClick={() => navigate(`/registration_new_client`)}
									/>
									<ButtonPlusElement />
								</div>
							</div>
							<div
								ref={clientSelectorRef}
								className={`${s.clientSelectorWrapper} ${
									highlightClientSelector ? s.highlightClientSelector : ''
								}`}
							>
								<ClientSelector
									initialClientId={
										data && id && isActivitiesData(data)
											? data.user_id?.toString()
											: undefined
									}
									initialClientName={
										data && id && isActivitiesData(data)
											? data.user_name
											: undefined
									}
									initialClientContract={
										data && id && isActivitiesData(data)
											? data.user_contract?.toString()
											: undefined
									}
									onClientChange={setSelectedClient}
								/>
							</div>

							{/* Селектор ФОП для фискализации */}
							<SupplierSelector
								onSupplierChange={setSelectedSupplierId}
								orderType={orderType}
								initialSupplierId={
									data && id && isActivitiesData(data) && data.supplier_id
										? data.supplier_id.toString()
										: undefined
								}
							/>

							<div
								className={`${style.categorySelectorContainer} ${s.categorySelectorContainer}`}
							>
								<CategorySelectorsList
									key={resetCategoriesKey}
									fieldName={'categoryId'}
									setCategoryId={setCategoryId}
								/>
							</div>
							<div className={s.buttonPanelWrapper}>
								<FunctionalButtonPanel
									callback={(id: string) => {
										if (id === 'all') {
											resetAllFilters();
										}
										handleClickButtonInPanel(id);
									}}
									functionality={functionalityButtons}
									elWidth={'0 0 175px'}
								/>
							</div>
							<div className={s.searchWindowWrapper}>
								<SearchByNameInput
									value={searchByName}
									onChange={handleChangeSearch}
								/>

								{/* Кнопка управления камерой */}
								<div className={s.cameraButtonWrapper}>
									<LightGreenButton
										type="button"
										text={
											isCameraActive
												? t('Зупинити камеру')
												: t('Запустити камеру')
										}
										onClick={handleToggleCamera}
										disabled={cameraPermission === 'denied'}
									/>

									<LightGreenButton
										type="button"
										text={t('Ввести вручну')}
										onClick={() => setShowManualInput(true)}
									/>

									{/* Иконка информации с подсказкой */}
									<div className={s.infoIconWrapper}>
										<img
											src={infoButton}
											alt={t('Інформація')}
											className={s.infoIcon}
											title={t(
												'Сканування баркодів: Наведіть камеру на баркод товару або використовуйте сканер. Товар автоматично додасться до списку.',
											)}
										/>
									</div>
								</div>

								{selectedProducts.length > 0 && selectedClient && (
									<LightGreenButton
										type={'button'}
										text={t('Підтвердити товари')}
										onClick={handleClickReadyButton}
									/>
								)}
							</div>

							{/* Видео элемент для камеры - всегда рендерим */}
							<div
								className={s.cameraVideoWrapper}
								style={{
									display: 'flex',
									maxHeight: isCameraActive ? '600px' : '0px',
									overflow: 'hidden',
									transition: 'max-height 0.3s ease',
									marginBottom: isCameraActive ? '20px' : '0',
								}}
							>
								<video
									ref={barcodeVideoRef}
									className={s.cameraVideo}
									playsInline
									muted
									autoPlay
								/>
							</div>

							{/* Поле для ручного ввода баркода */}
							{showManualInput && (
								<div className={s.manualInputWrapper}>
									<div className={s.manualInputHeader}>
										<h4>{t('Ручний ввід баркоду')}</h4>
										<button
											className={s.closeInputButton}
											onClick={() => setShowManualInput(false)}
											type="button"
											aria-label={t('Закрити')}
										>
											✕
										</button>
									</div>

									<div className={s.manualInputContent}>
										{lastScannedBarcode && (
											<div className={s.lastScannedInfo}>
												<p>
													<strong>{t('Останній відсканований баркод:')}</strong>
												</p>
												<p className={s.barcodeValue}>{lastScannedBarcode}</p>
											</div>
										)}

										<div className={s.inputSection}>
											<label htmlFor="manualBarcodeInput">
												{t('Введіть баркод товару:')}
											</label>
											<input
												ref={barcodeInputRef}
												id="manualBarcodeInput"
												type="text"
												placeholder={t('Наприклад: 1234567890123')}
												onChange={handleBarcodeInputChange}
												onKeyPress={(e) => {
													if (e.key === 'Enter') {
														const input = barcodeInputRef.current;
														if (input && input.value.trim()) {
															addProductByBarcode(input.value.trim());
															input.value = '';
															setShowManualInput(false);
														}
													}
												}}
												className={s.manualBarcodeInput}
												autoFocus
											/>
										</div>
									</div>

									<div className={s.manualInputActions}>
										<VeryLightGreenButton
											text={t('Скасувати')}
											onClick={() => setShowManualInput(false)}
											type="button"
										/>
										<LightGreenButton
											text={t('Додати товар')}
											onClick={() => {
												const input = barcodeInputRef.current;
												if (input && input.value.trim()) {
													addProductByBarcode(input.value.trim());
													input.value = '';
													setShowManualInput(false);
												}
											}}
											type="button"
										/>
									</div>
								</div>
							)}
							<div className={style.tableWrapper}>
								{productsForRender && (
									<DepotProductsList
										isLoading={isLoadingProductList}
										products={productsForRender}
										changeProductCallback={handleChangeProduct}
									/>
								)}
								{createPopup &&
									selectedProducts.length > 0 &&
									selectedClient && (
										<CreateDesignPopup
											close={closePopup}
											prepareProducts={selectedProducts}
											changeProductCallback={handleChangeProduct}
											client={selectedClient}
										/>
									)}
							</div>
						</Form>
					)}
				</Formik>
			</div>
		</main>
	);
};
export default CreationOfDesign;
